import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard,
  TrendingUp,
  Tag,
  Search,
  Plus,
  Trash2,
  Power,
  AlertTriangle,
  DollarSign,
  Users,
  Percent,
  Activity,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";

import {
  adminRevenueAnalytics,
  adminGetPricingPlans,
  adminUpdatePricingPlans,
  adminListPromoCodes,
  adminCreatePromoCode,
  adminTogglePromoCode,
  adminDeletePromoCode,
  adminSearchUserSubscription,
  adminCancelSubscription,
  adminListUserRefundEvents,
} from "@/lib/admin-subscriptions.functions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Receipt } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/subscriptions")({
  component: SubscriptionsTab,
});

function SubscriptionsTab() {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5">
        <div className="rounded-md border border-primary/40 bg-primary/10 p-1.5 text-primary">
          <CreditCard className="h-4 w-4" />
        </div>
        <h2 className="text-lg font-bold tracking-tight">
          Subscription & Revenue Control
        </h2>
      </div>

      <Tabs defaultValue="analytics" className="space-y-5">
        <TabsList>
          <TabsTrigger value="analytics">
            <TrendingUp className="mr-2 h-3.5 w-3.5" />
            Revenue
          </TabsTrigger>
          <TabsTrigger value="plans">
            <DollarSign className="mr-2 h-3.5 w-3.5" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="promos">
            <Tag className="mr-2 h-3.5 w-3.5" />
            Promo Codes
          </TabsTrigger>
          <TabsTrigger value="lookup">
            <Search className="mr-2 h-3.5 w-3.5" />
            User Lookup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics">
          <RevenuePanel />
        </TabsContent>
        <TabsContent value="plans">
          <PlansPanel />
        </TabsContent>
        <TabsContent value="promos">
          <PromoPanel />
        </TabsContent>
        <TabsContent value="lookup">
          <LookupPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Revenue ---------------- */
function RevenuePanel() {
  const fn = useServerFn(adminRevenueAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "revenue"],
    queryFn: () => fn(),
  });

  if (isLoading || !data) {
    return <div className="text-sm text-muted-foreground">Loading revenue…</div>;
  }

  const cards = [
    { label: "MRR", value: `$${data.mrr.toLocaleString()}`, icon: DollarSign },
    { label: "Paying users", value: data.payingUsers.toString(), icon: Users },
    { label: "ARPU", value: `$${data.arpu.toFixed(2)}`, icon: Activity },
    { label: "Churn (mo)", value: `${data.churnPct}%`, icon: Percent },
    { label: "Est. LTV", value: `$${data.ltv.toLocaleString()}`, icon: TrendingUp },
    {
      label: "Failed payments",
      value: data.failedPayments.toString(),
      icon: AlertTriangle,
    },
  ];

  const planColors: Record<string, string> = {
    free: "hsl(var(--muted-foreground))",
    pro: "hsl(var(--primary))",
    elite: "hsl(var(--warn, 38 92% 50%))",
  };
  const planPie = Object.entries(data.planDist).map(([name, value]) => ({
    name,
    value,
    fill: planColors[name] ?? "hsl(var(--muted))",
  }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <Card key={c.label} className="border-border/60 bg-card/60 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {c.label}
              </span>
              <c.icon className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="mt-2 text-xl font-bold tabular-nums">{c.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-border/60 bg-card/60 p-5 lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold">MRR — last 6 months</h3>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.mrrSeries}>
                <defs>
                  <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <RTooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="mrr"
                  stroke="hsl(var(--primary))"
                  fill="url(#mrrGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border-border/60 bg-card/60 p-5">
          <h3 className="mb-3 text-sm font-semibold">Plan distribution</h3>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={planPie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                >
                  {planPie.map((entry, idx) => (
                    <Cell key={idx} fill={entry.fill} />
                  ))}
                </Pie>
                <RTooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex justify-center gap-3 text-xs">
            {planPie.map((p) => (
              <span key={p.name} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: p.fill }}
                />
                <span className="capitalize text-muted-foreground">{p.name}</span>
                <span className="font-semibold">{p.value}</span>
              </span>
            ))}
          </div>
        </Card>
      </div>

      <Card className="border-border/60 bg-card/60 p-5">
        <h3 className="mb-3 text-sm font-semibold">MRR by plan</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(data.mrrByPlan).map(([plan, mrr]) => (
            <div
              key={plan}
              className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 px-3 py-2"
            >
              <span className="text-xs capitalize text-muted-foreground">{plan}</span>
              <span className="font-bold tabular-nums">${mrr.toLocaleString()}/mo</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Plans editor ---------------- */
type Plan = {
  id: "free" | "pro" | "elite";
  name: string;
  price: number;
  trial_days: number;
  features: string[];
};

function PlansPanel() {
  const qc = useQueryClient();
  const fn = useServerFn(adminGetPricingPlans);
  const save = useServerFn(adminUpdatePricingPlans);
  const { data } = useQuery({ queryKey: ["admin", "plans"], queryFn: () => fn() });
  const [drafts, setDrafts] = useState<Plan[]>([]);

  useEffect(() => {
    if (data?.plans) setDrafts(data.plans as Plan[]);
  }, [data]);

  const mut = useMutation({
    mutationFn: (plans: Plan[]) => save({ data: { plans } }),
    onSuccess: () => {
      toast.success("Plans saved");
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = (idx: number, patch: Partial<Plan>) => {
    setDrafts((d) => d.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Pricing displayed on the landing page. Editing here only updates display;
        live Stripe prices are managed in the Stripe connector.
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        {drafts.map((plan, idx) => (
          <Card key={plan.id} className="border-border/60 bg-card/60 p-5">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="font-mono uppercase">
                {plan.id}
              </Badge>
              <span className="text-xs text-muted-foreground">${plan.price}/mo</span>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs">Display name</Label>
                <Input
                  className="mt-1"
                  value={plan.name}
                  onChange={(e) => update(idx, { name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Price ($)</Label>
                  <Input
                    className="mt-1"
                    type="number"
                    min={0}
                    value={plan.price}
                    onChange={(e) =>
                      update(idx, { price: Number(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">Trial (days)</Label>
                  <Input
                    className="mt-1"
                    type="number"
                    min={0}
                    value={plan.trial_days}
                    onChange={(e) =>
                      update(idx, { trial_days: Number(e.target.value) || 0 })
                    }
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Features (one per line)</Label>
                <Textarea
                  className="mt-1 min-h-[120px] font-mono text-xs"
                  value={plan.features.join("\n")}
                  onChange={(e) =>
                    update(idx, {
                      features: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>
            </div>
          </Card>
        ))}
      </div>
      <div className="flex justify-end">
        <Button onClick={() => mut.mutate(drafts)} disabled={mut.isPending}>
          {mut.isPending ? "Saving…" : "Save all plans"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Promo Codes ---------------- */
function PromoPanel() {
  const qc = useQueryClient();
  const list = useServerFn(adminListPromoCodes);
  const create = useServerFn(adminCreatePromoCode);
  const toggle = useServerFn(adminTogglePromoCode);
  const del = useServerFn(adminDeletePromoCode);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "promos"],
    queryFn: () => list(),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "",
    percent_off: 10,
    max_redemptions: "",
    expires_at: "",
    notes: "",
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "promos"] });

  const createMut = useMutation({
    mutationFn: () =>
      create({
        data: {
          code: form.code.trim().toUpperCase(),
          percent_off: form.percent_off,
          max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
          notes: form.notes || null,
        },
      }),
    onSuccess: () => {
      toast.success("Promo created");
      setOpen(false);
      setForm({ code: "", percent_off: 10, max_redemptions: "", expires_at: "", notes: "" });
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => toggle({ data: v }),
    onSuccess: refresh,
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Promo deleted");
      setDeleteId(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {data?.codes.length ?? 0} promo code{data?.codes.length === 1 ? "" : "s"}
        </p>
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          New code
        </Button>
      </div>

      <Card className="border-border/60 bg-card/60">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-xs text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data?.codes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-xs text-muted-foreground">
                  No promo codes yet.
                </TableCell>
              </TableRow>
            )}
            {data?.codes.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                <TableCell>{c.percent_off}%</TableCell>
                <TableCell>
                  {c.times_redeemed}
                  {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={c.active}
                    onCheckedChange={(v) =>
                      toggleMut.mutate({ id: c.id, active: v })
                    }
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setDeleteId(c.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New promo code</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs">Code</Label>
              <Input
                className="mt-1 font-mono uppercase"
                placeholder="LAUNCH20"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">% off</Label>
                <Input
                  className="mt-1"
                  type="number"
                  min={1}
                  max={100}
                  value={form.percent_off}
                  onChange={(e) =>
                    setForm({ ...form, percent_off: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <Label className="text-xs">Max uses (optional)</Label>
                <Input
                  className="mt-1"
                  type="number"
                  min={1}
                  value={form.max_redemptions}
                  onChange={(e) =>
                    setForm({ ...form, max_redemptions: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Expires (optional)</Label>
              <Input
                className="mt-1"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                className="mt-1 min-h-[60px]"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promo code?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Existing redemptions remain in history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => deleteId && deleteMut.mutate(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- User Lookup ---------------- */
type RefundStatus = "none" | "partial" | "full";

function refundBadgeVariant(status: RefundStatus): "secondary" | "outline" | "destructive" {
  if (status === "full") return "destructive";
  if (status === "partial") return "outline";
  return "secondary";
}

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount == null) return "—";
  const cur = (currency ?? "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${cur}`;
  }
}

function LookupPanel() {
  const search = useServerFn(adminSearchUserSubscription);
  const cancel = useServerFn(adminCancelSubscription);
  const listRefunds = useServerFn(adminListUserRefundEvents);

  const [q, setQ] = useState("");
  const [results, setResults] = useState<Awaited<ReturnType<typeof search>>["results"]>([]);
  const [busy, setBusy] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [refundUserId, setRefundUserId] = useState<string | null>(null);
  const [refundEvents, setRefundEvents] = useState<
    Awaited<ReturnType<typeof listRefunds>>["events"]
  >([]);
  const [refundLoading, setRefundLoading] = useState(false);

  const run = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await search({ data: { query: q.trim() } });
      setResults(r.results);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openRefundDrawer = async (userId: string) => {
    setRefundUserId(userId);
    setRefundLoading(true);
    try {
      const r = await listRefunds({ data: { userId } });
      setRefundEvents(r.events);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRefundLoading(false);
    }
  };

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancel({ data: { subscriptionId: id } }),
    onSuccess: () => {
      toast.success("Subscription canceled");
      setCancelId(null);
      run();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Search by email, name, or user ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
        />
        <Button onClick={run} disabled={busy}>
          <Search className="mr-1.5 h-3.5 w-3.5" />
          Search
        </Button>
      </div>

      <Card className="border-border/60 bg-card/60">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Renews</TableHead>
              <TableHead>Refund</TableHead>
              <TableHead>Captured / Refunded</TableHead>
              <TableHead>Last refund</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-xs text-muted-foreground">
                  {q ? "No matches." : "Enter a query to find users."}
                </TableCell>
              </TableRow>
            )}
            {results.map((r) => {
              const refund = r.refundSummary;
              const status = (refund?.status ?? "none") as RefundStatus;
              const isRefunded = r.subscription?.status === "refunded";
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-medium">{r.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.display_name ?? "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {r.plan}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {r.subscription ? (
                      <Badge
                        variant={
                          isRefunded
                            ? "destructive"
                            : r.subscription.status === "active" ||
                                r.subscription.status === "trialing"
                              ? "default"
                              : "secondary"
                        }
                      >
                        {r.subscription.status}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">none</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.subscription?.current_period_end
                      ? new Date(r.subscription.current_period_end).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant={refundBadgeVariant(status)}
                          className={
                            status === "partial"
                              ? "border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              : ""
                          }
                        >
                          {status}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Partial refunds do not automatically downgrade access.
                        Full or cumulative full refunds downgrade to Free.
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="text-xs tabular-nums text-muted-foreground">
                    {refund && refund.amountCaptured != null
                      ? `${formatMoney(refund.amountCaptured, refund.currency)} / ${formatMoney(
                          refund.amountRefunded,
                          refund.currency,
                        )}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {refund?.lastRefundAt ? (
                      <div>
                        <div>{new Date(refund.lastRefundAt).toLocaleDateString()}</div>
                        <div className="capitalize">{refund.lastRefundType}</div>
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {status !== "none" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRefundDrawer(r.id)}
                        >
                          <Receipt className="mr-1.5 h-3.5 w-3.5" />
                          Refunds
                        </Button>
                      )}
                      {r.subscription && r.subscription.status !== "canceled" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCancelId(r.subscription!.id)}
                        >
                          <Power className="mr-1.5 h-3.5 w-3.5" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog
        open={!!refundUserId}
        onOpenChange={(open) => {
          if (!open) {
            setRefundUserId(null);
            setRefundEvents([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Refund audit events</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Partial refunds do not automatically downgrade access. Full or
            cumulative full refunds downgrade to Free.
          </p>
          {refundLoading ? (
            <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
          ) : refundEvents.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No refund events for this user.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Charge</TableHead>
                    <TableHead>Env</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {refundEvents.map((ev, i) => (
                    <TableRow key={`${ev.charge_id ?? "ev"}-${i}`}>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(ev.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            ev.action === "FULL_REFUND_DOWNGRADED"
                              ? "destructive"
                              : ev.action === "PARTIAL_REFUND_RECORDED"
                                ? "outline"
                                : "secondary"
                          }
                          className={
                            ev.action === "PARTIAL_REFUND_RECORDED"
                              ? "border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              : ""
                          }
                        >
                          {ev.action.toLowerCase().replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {formatMoney(ev.amount_refunded, ev.currency)} /{" "}
                        {formatMoney(ev.amount_captured, ev.currency)}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">
                        {ev.charge_id ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {ev.environment ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundUserId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Marks the subscription as canceled in our database. To revoke billing in
              Stripe, also cancel it from the Stripe dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => cancelId && cancelMut.mutate(cancelId)}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
